import { DateTime } from 'luxon';

/**
 * Formats a subscription period boundary — renewal, paid-through, or
 * access-ends date — for display. Returns null when there's no date so callers
 * can drop the line rather than render an empty one.
 */
export const formatPlanPeriodDate = (iso?: string | null): string | null =>
  iso
    ? // Read in UTC, not the viewer's zone: a manually granted subscription
      // stores the operator's calendar date at midnight UTC, so converting it
      // locally shows the day before anywhere west of UTC. Stripe's own period
      // ends are true instants, and pinning them to UTC keeps every viewer —
      // and support — quoting the same renewal date.
      DateTime.fromISO(iso, { zone: 'utc' }).toLocaleString({
        month: 'long',
        day: 'numeric',
        year: 'numeric',
      })
    : null;

/**
 * The same, but only for a boundary that has actually passed — for saying when
 * something *ended*.
 *
 * A terminal subscription usually carries a period end in the past, but not
 * always: cancelling immediately rather than at period end leaves the row with
 * its unreached period end, and "Ended <a date next year>" is nonsense. Callers
 * fall back to a date-less sentence when this returns null.
 */
export const formatElapsedPlanPeriodDate = (
  iso?: string | null
): string | null => {
  if (!iso) {
    return null;
  }

  // Parsed in UTC to match formatPlanPeriodDate below: a date-only value (what a
  // manual grant stores) would otherwise be read as local midnight here and UTC
  // midnight there, which can disagree about whether it has passed.
  const date = DateTime.fromISO(iso, { zone: 'utc' });

  // An unparseable value compares false against every date, so without this it
  // would fall through and render the literal string "Invalid DateTime".
  if (!date.isValid || date > DateTime.now()) {
    return null;
  }

  return formatPlanPeriodDate(iso);
};
